import { useEffect, useRef, type PointerEvent } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field } from './Field';
import { fieldHintId } from './fieldParts';

/** Couleur de l'encre (bleu foncé, comme un stylo) */
const INK = '#1e3a8a';

/**
 * Signature dessinée à la souris, au doigt ou au stylet, dans un canvas : onChange reçoit l'image (PNG, fond
 * transparent) à la fin de chaque trait, null une fois effacée. Résolution adaptée à l'écran (devicePixelRatio).
 */
export function SignaturePad({
  id,
  label = 'Signature',
  onChange,
}: {
  id: string;
  label?: string;
  onChange: (image: Blob | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  // Taille réelle du canvas = taille affichée × densité de l'écran, sinon le trait est flou
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = INK;
  }, []);

  const pointOf = (event: PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return [event.clientX - rect.left, event.clientY - rect.top] as const;
  };

  const start = (event: PointerEvent<HTMLCanvasElement>) => {
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const [x, y] = pointOf(event);
    context.beginPath();
    context.moveTo(x, y);
    // Un point seul (clic sans bouger) laisse une trace
    context.lineTo(x + 0.1, y + 0.1);
    context.stroke();
  };

  const move = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const context = event.currentTarget.getContext('2d');
    if (!context) return;
    const [x, y] = pointOf(event);
    context.lineTo(x, y);
    context.stroke();
  };

  const end = (event: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    event.currentTarget.toBlob((image) => onChange(image), 'image/png');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    onChange(null);
  };

  return (
    <Field id={id} label={label} required hint="Signez dans le cadre, à la souris ou au doigt.">
      <div className="relative">
        <canvas
          id={id}
          ref={canvasRef}
          role="img"
          aria-label="Zone de signature"
          aria-describedby={fieldHintId(id)}
          className="block w-full h-40 rounded-lg border border-dashed border-slate-300 bg-slate-50 dark:bg-white touch-none cursor-crosshair"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
        />
        {/* En mode nuit, le cadre reste du papier blanc (encre bleu nuit) : bouton en noir sur blanc */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-1 right-1 dark:text-black dark:hover:bg-black/5"
          onClick={clear}
        >
          <Eraser className="w-4 h-4" />
          Effacer
        </Button>
      </div>
    </Field>
  );
}
